import { inject, Injectable } from '@angular/core';
import axios from 'axios';
import { COMMONS_ENV, NetworkService } from '@damofire/data-angular-commons';
import { collection, Firestore, getDocs, query } from '@angular/fire/firestore';
import { captureMessage } from '@sentry/angular';

@Injectable()
export class BackendPokerService {
  private readonly environment = inject(COMMONS_ENV);
  private readonly networkService = inject(NetworkService);
  private readonly firestore = inject(Firestore);

  pokeBackend(): Promise<{ storage: boolean; db: boolean }> {
    if (this.environment.target === 'app') {
      return this.checkAppStatus();
    }

    return this.checkStatus();
  }

  async checkFirestore(): Promise<boolean> {
    console.log('Calling checkFirestore');
    const isOnline = await this.networkService.isAppOnline();
    // Exit early if offline or not targeting 'app'.
    if (!isOnline || this.environment.target !== 'app') {
      return true;
    }

    try {
      await getDocs(query(collection(this.firestore, 'app-health')));
    } catch (e) {
      console.error(e);
      // Check for unrecoverable errors. Kill native listeners to avoid memory leaks and reload app.
      const errorString = JSON.stringify(e).toLowerCase();
      if (errorString.includes('unexpected state') || errorString.includes('internal assertion failed')) {
        console.error('Reboot app since firestore is not working with internet on.');
        captureMessage('Reboot app since firestore is not working with internet on.', {
          level: 'error',
          tags: {
            isReboot: true,
          },
        });
        const cap = await import('@capacitor/app');
        await cap.App.removeAllListeners();
        location.reload();
      }
    }
    return true;
  }

  private checkAppStatus(): Promise<{ storage: boolean; db: boolean }> {
    return this.networkService.isAppOnline().then((online: boolean) => {
      if (!online) {
        return Promise.resolve({ storage: true, db: true });
      } else {
        return this.checkStatus();
      }
    });
  }

  private checkStatus(): Promise<{ storage: boolean; db: boolean }> {
    return Promise.all([this.checkDB(), this.checkStorage()]).then(([db, storage]) => {
      return { db, storage };
    });
  }

  private async checkStorage(): Promise<boolean> {
    try {
      await axios.get(this.environment.appConfig.heartbeatUrl, { responseType: 'json' });
    } catch (e) {
      console.error(e);
      return false;
    }
    return true;
  }

  private async checkDB(): Promise<boolean> {
    try {
      const configs = await getDocs(query(collection(this.firestore, 'app-health')));
      return !!configs?.size;
    } catch (e) {
      console.error(e);
      return (e as { code: string }).code === 'permission-denied';
    }
  }
}
